import React, { useState } from 'react'
import { StyleSheet } from 'react-native'

import { WebDatePicker } from '@zet/design-system'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import moment from 'moment'

import CalendarIcon from '../../assets/icons/calendar'
import CrossRed from '../../assets/icons/crossRed'
import { M14, R14 } from '../typography'

const computeDate = (date?: string, durationInYears?: number) => {
  if (date) {
    if (date === 'CURRENT_DATE') {
      return moment().format('YYYY-MM-DD')
    }
    return moment(date).format('YYYY-MM-DD')
  } else if (durationInYears) {
    if (durationInYears < 0) {
      return moment()
        .subtract(Math.abs(durationInYears), 'years')
        .format('YYYY-MM-DD')
    } else {
      return moment().add(durationInYears, 'years').format('YYYY-MM-DD')
    }
  }
}

interface Props {
  label: string
  onChange: (date: string) => void
  onBlur: () => void
  value: string
  errors: any
  isRequired?: boolean
  config?: {
    minDateDuration?: number // In years
    maxDateDuration?: number // In years
    minDate?: string
    maxDate?: string
    required?: boolean
  }
  errorMessage: string
  example: string
}

export const DateSelector = ({
  label,
  onChange,
  onBlur,
  value,
  errors,
  config,
  errorMessage,
  example,
}: Props) => {
  const [isFocused, setIsFocused] = useState(false)
  const minDate = computeDate(config?.minDate, config?.minDateDuration)
  const maxDate = computeDate(config?.maxDate, config?.maxDateDuration)

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = () => {
    setIsFocused(false)
    onBlur()
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsFocused(true) // Keep focused state when user is interacting
    onChange(e.target.value)
  }

  return (
    <Stack className="mt-1">
      <M14
        className={`mb-2 ${
          !isFocused && (errors?.name?.message || errorMessage)
            ? 'text-error-100'
            : ''
        }`}
      >
        {label}
      </M14>
      <Pressable onPress={handleFocus}>
        <XStack
          className={`items-center justify-between rounded-lg bg-white ${
            isFocused
              ? 'border-[1.5px] border-primary-150'
              : errorMessage
                ? 'border-[2px] border-error-100'
                : 'border-[1.5px] border-[#191B1E52]'
          }`}
        >
          <WebDatePicker
            onChange={handleChange}
            onBlur={handleBlur}
            value={value ? value : ''}
            minDate={minDate ?? null}
            maxDate={maxDate ?? null}
            style={styles.datePicker}
            isWebView={true}
            placeholder="DD/MM/YYYY"
            showCustomPlaceholder={true}
            placeholderStyle={styles.placeholder}
          />
          <CalendarIcon className="absolute right-3" />
        </XStack>
      </Pressable>
      {!isFocused && (errors?.name?.message || errorMessage) ? (
        <XStack className="mt-2">
          <CrossRed className="mr-2" />
          <R14 className="text-black">
            {errors?.name?.message || errorMessage}
          </R14>
        </XStack>
      ) : null}
      {/* {!value && example ? (
        <B14 className="text-grays-80 mt-2">{example}</B14>
      ) : null} */}
    </Stack>
  )
}

const styles = StyleSheet.create({
  datePicker: {
    fontSize: 14,
    fontFamily: 'JioType-Medium',
    paddingVertical: 14,
    width: '100%',
    paddingLeft: 12,
    zIndex: 1,
    borderWidth: 0,
  },
  placeholder: {
    left: 12,
    color: '#24262BA6',
  },
})
